import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Divider,
  VStack,
  HStack,
  Image,
  Input,
  FormLabel,
  Select,
  FormControl,
  useColorModeValue,
  Grid,
  GridItem,
  Card,
  CardBody,
  CardHeader,
  useToast,
} from "@chakra-ui/react";
import { useShopping } from "../context/ShoppingContext";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";

const Checkout = () => {
  const { cart, getCartTotal, clearCart } = useShopping();
  const navigate = useNavigate();
  const toast = useToast();
  const [address, setAddress] = useState("");
  const [payment, setPayment] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const bg = useColorModeValue("gray.50", "gray.800");
  const cardBg = useColorModeValue("white", "gray.700");

  const generateQuotationPDF = () => {
    const doc = new jsPDF();

    // Set up document
    doc.setFontSize(20);
    doc.text("NOVATRA - Quotation", 20, 30);

    doc.setFontSize(12);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 45);

    if (email) {
      doc.text(`Customer Email: ${email}`, 20, 55);
    }
    if (phone) {
      doc.text(`Phone: ${phone}`, 20, 65);
    }
    if (address) {
      doc.text(`Shipping Address: ${address}`, 20, 75);
    }

    // Items header
    doc.setFontSize(14);
    doc.text("Items:", 20, 95);

    // Table headers
    doc.setFontSize(10);
    doc.text("Item", 20, 110);
    doc.text("Qty", 120, 110);
    doc.text("Price", 150, 110);
    doc.text("Total", 180, 110);

    // Draw line
    doc.line(20, 115, 190, 115);

    let yPosition = 125;
    cart.forEach((item) => {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 30;
      }

      // Item name (truncate if too long)
      const itemNameValue = item.name || item.materialName || item.materialCode || "Material";
      const itemName = itemNameValue.length > 25 ? itemNameValue.substring(0, 25) + "..." : itemNameValue;
      doc.text(itemName, 20, yPosition);

      // Quantity
      doc.text(item.quantity.toString(), 120, yPosition);

      // Price
      const itemPrice = Number(item.price ?? item.mrp ?? item.baseSellingPrice ?? 0);
      doc.text(`₹${itemPrice.toLocaleString("en-IN")}`, 150, yPosition);

      // Item total
      const itemTotal = itemPrice * item.quantity;
      doc.text(`₹${itemTotal}`, 180, yPosition);

      yPosition += 10;
    });

    // Summary
    yPosition += 10;
    doc.line(20, yPosition, 190, yPosition);
    yPosition += 10;

    const subtotal = getCartTotal();
    doc.text(`Subtotal: ₹${subtotal.toLocaleString()}`, 140, yPosition);
    yPosition += 10;
    doc.text("Shipping: Free", 140, yPosition);
    yPosition += 10;
    doc.line(140, yPosition, 190, yPosition);
    yPosition += 10;
    doc.setFontSize(12);
    doc.text(`Total: ₹${subtotal.toLocaleString()}`, 140, yPosition);

    // Terms
    yPosition += 20;
    if (yPosition > 250) {
      doc.addPage();
      yPosition = 30;
    }
    doc.setFontSize(10);
    doc.text("Terms & Conditions:", 20, yPosition);
    yPosition += 10;
    doc.text(
      "• This quotation is valid for 30 days from the date of issue.",
      20,
      yPosition,
    );
    yPosition += 8;
    doc.text(
      "• Prices are subject to change without prior notice.",
      20,
      yPosition,
    );
    yPosition += 8;
    doc.text("• Payment terms: As selected during checkout.", 20, yPosition);

    // Save the PDF
    doc.save(`Novatra_Quotation_${new Date().toISOString().split("T")[0]}.pdf`);
  };

  const handlePlaceOrder = async () => {
    if (!email || !address || !phone || !payment) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      const orderData = {
        orderId: `ORD-${Date.now()}`, // Generate unique order ID
        store: "Novatra", // Store name
        items: cart.map((item) => ({
          sku: item.id || item._id,
          name: item.name || item.materialName || "Material",
          qty: item.quantity,
          price: Number(item.price ?? item.mrp ?? item.baseSellingPrice ?? 0),
          tax: 0,
        })),
        billAmount: getCartTotal(),
        shippingAddress: address,
        paymentMethod: payment,
        customerEmail: email,
        customerPhone: phone,
        orderDate: new Date().toISOString(),
        metadata: {
          shippingAddress: address,
          paymentMethod: payment,
          customerEmail: email,
          customerPhone: phone,
        },
      };

      const response = await fetch(
        "http://localhost:4001/api/orders/createOrder",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(orderData),
        },
      );

      const result = await response.json();

      if (result.success) {
        clearCart();
        toast({
          title: "Order Placed Successfully!",
          description: "Your order has been placed and will be processed soon.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        navigate("/");
      } else {
        throw new Error(result.message || "Failed to place order");
      }
    } catch (error) {
      console.error("Order placement error:", error);
      toast({
        title: "Order Failed",
        description: error.message || "Something went wrong. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxW="95%" py={10}>
      <Heading mb={8} textAlign="center">
        Checkout
      </Heading>
      {cart.length === 0 ? (
        <Text textAlign="center">
          Your cart is empty. Please add items before checking out.
        </Text>
      ) : (
        <Grid templateColumns={{ base: "1fr", md: "2fr 1fr" }} gap={8}>
          {/* Left: Address & Payment */}
          <GridItem>
            <Card bg={cardBg} boxShadow="md" borderRadius="xl">
              <CardHeader>
                <Heading size="md">Contact & Shipping</Heading>
              </CardHeader>
              <CardBody>
                <FormControl mb={6} isRequired>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </FormControl>
                <FormControl mb={6} isRequired>
                  <FormLabel>Shipping Address</FormLabel>
                  <Input
                    placeholder="Enter your shipping address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                  />
                </FormControl>
                <FormControl mb={6} isRequired>
                  <FormLabel>Phone</FormLabel>
                  <Input
                    type="tel"
                    placeholder="Enter your phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </FormControl>
                <Heading size="sm" mb={2}>
                  Payment Method
                </Heading>
                <FormControl mb={6} isRequired>
                  <FormLabel>Payment</FormLabel>
                  <Select
                    placeholder="Select payment method"
                    value={payment}
                    onChange={(e) => setPayment(e.target.value)}
                  >
                    <option value="cod">Cash on Delivery</option>
                    <option value="card">Credit/Debit Card</option>
                    <option value="upi">UPI</option>
                  </Select>
                </FormControl>
                <Button
                  colorScheme="teal"
                  size="lg"
                  w="full"
                  isDisabled={!email || !address || !phone || !payment}
                  onClick={handlePlaceOrder}
                  isLoading={isLoading}
                  loadingText="Placing Order..."
                  mt={2}
                >
                  Place Order
                </Button>
                <Button
                  variant="ghost"
                  mt={2}
                  w="full"
                  onClick={() => navigate("/cart")}
                >
                  Back to Cart
                </Button>
              </CardBody>
            </Card>
          </GridItem>
          {/* Right: Order Summary */}
          <GridItem>
            <Card bg={cardBg} boxShadow="md" borderRadius="xl">
              <CardHeader>
                <Heading size="md">Order Summary</Heading>
              </CardHeader>
              <CardBody>
                <VStack align="stretch" spacing={4} mb={4}>
                  {cart.map((item) => (
                    <HStack
                      key={item.id}
                      spacing={4}
                      align="center"
                      bg={useColorModeValue("gray.50", "gray.800")}
                      p={3}
                      borderRadius="md"
                      boxShadow="sm"
                    >
                      <Image
                        src={item.image}
                        alt={item.name}
                        boxSize="56px"
                        borderRadius="md"
                      />
                      <Box flex={1}>
                        <Text fontWeight="bold">{item.name}</Text>
                        <Text fontSize="sm" color="gray.500">
                          Qty: {item.quantity}
                        </Text>
                      </Box>
                      <Text fontWeight="bold" color="blue.600">
                        {item.price}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
                <Divider mb={4} />
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="bold">Subtotal</Text>
                  <Text fontWeight="bold" color="teal.600">
                    ₹{getCartTotal().toLocaleString()}
                  </Text>
                </HStack>
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="bold">Shipping</Text>
                  <Text color="gray.500">Free</Text>
                </HStack>
                <Divider mb={2} />
                <HStack
                  justify="space-between"
                  fontSize="xl"
                  fontWeight="bold"
                  color="teal.700"
                >
                  <Text>Total</Text>
                  <Text>₹{getCartTotal().toLocaleString()}</Text>
                </HStack>
                <Button
                  colorScheme="blue"
                  variant="outline"
                  size="md"
                  w="full"
                  mt={4}
                  onClick={generateQuotationPDF}
                  leftIcon={<span>📄</span>}
                >
                  Download Quotation (PDF)
                </Button>
              </CardBody>
            </Card>
          </GridItem>
        </Grid>
      )}
    </Container>
  );
};

export default Checkout;
